import { DatabaseConnectionManager } from "@/domains/database";
import type { PGlite } from "@electric-sql/pglite";
import { useCallback, useEffect, useState, ComponentType } from "react";
import type { AppStore } from "@/store";

type ViewState = "checking" | "not-initialized" | "ready" | "error";

/**
 * Hook to manage database connection state and access to the PGlite instance
 */
export const useDatabase = ({ database }: { database: AppStore["database"] }) => {
  const [pg, setPg] = useState<PGlite | null>(null);
  const [viewState, setViewState] = useState<ViewState>("checking");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const evaluateDatabaseState = useCallback(() => {
    let nextPg: PGlite | null = null;
    let nextViewState: ViewState = "not-initialized";
    let nextError: string | null = null;

    if (database.isInitialized()) {
      const connectionManager = DatabaseConnectionManager.getActiveInstance();

      if (connectionManager && connectionManager.isReady()) {
        try {
          nextPg = connectionManager.getDatabase();
          nextViewState = "ready";
        } catch (error) {
          nextViewState = "error";
          nextError =
            error instanceof Error ? error.message : "Failed to access database instance.";
        }
      }
    }

    setPg(nextPg);
    setViewState(nextViewState);
    setErrorMessage(nextError);
  }, [database]);

  useEffect(() => {
    evaluateDatabaseState();
  }, [evaluateDatabaseState]);

  return {
    pg,
    viewState,
    errorMessage,
    refresh: evaluateDatabaseState,
  };
};

/**
 * Hook to dynamically load the PGlite REPL component (dev only)
 */
export const useReplComponent = () => {
  const [ReplComponent, setReplComponent] = useState<ComponentType<{ pg: PGlite }> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (import.meta.env.DEV) {
      import("@electric-sql/pglite-repl")
        .then(mod => {
          setReplComponent(() => mod.Repl);
        })
        .catch(error => {
          const message = error instanceof Error ? error.message : "Failed to load REPL.";
          setLoadError(message);
        });
    }
  }, []);

  return { ReplComponent, loadError };
};
